import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Clock, MapPin, Zap, Truck, Navigation, Layers, PhoneCall } from 'lucide-react';
import { useMetadata } from '../hooks/useMetadata';
import { servicesData } from '../data';

export default function Services() {
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/ydelser')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente ydelser fra CMS');
        return res.json();
      })
      .then((data) => {
        if (data && data.collectionName === 'pages') {
          setCmsPage(data);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  // Brug metadata fra CMS eller fallback
  const title = cmsPage?.meta_title || 'Ydelser - FLOW Transport & Logistik ApS';
  const description =
    cmsPage?.meta_description ||
    'Se vores brede udvalg af transport- og logistikydelser. Vi tilbyder kurerkørsel, distribution og logistikløsninger tilpasset dine behov.';

  useMetadata(title, description);

  // Hjælpefunktion til at hente specifikke fakta per ydelse til faktaboksen
  const getServiceSpecs = (id) => {
    switch (id) {
      case 'kurer':
        return [
          { label: 'Dækningsområde', value: 'Hele landet' },
          { label: 'Responstid', value: 'Hurtig afhentning' },
          { label: 'Sikkerhed', value: 'Direkte levering uden omlastning' },
          { label: 'Oplagt til', value: 'Hasteopgaver & dokumenter' }
        ];
      case 'distribution':
        return [
          { label: 'Dækningsområde', value: 'Landsdækkende' },
          { label: 'Leveringstid', value: 'Dag-til-dag levering' },
          { label: 'Godstyper', value: 'Paller & stykgods' },
          { label: 'Fleksibilitet', value: 'Faste leveringsvinduer' }
        ];
      case 'special':
        return [
          { label: 'Sikkerhed', value: 'Fuld forsikringsdækning' },
          { label: 'Udstyr', value: 'Skræddersyet fastspænding' },
          { label: 'Chauffører', value: 'Særligt uddannede' },
          { label: 'Godstyper', value: 'Følsomt & værdifuldt gods' }
        ];
      case 'fast-rute':
        return [
          { label: 'Planlægning', value: 'Faste ugentlige/daglige afgange' },
          { label: 'Kapacitet', value: 'Garanteret faste biler' },
          { label: 'Chauffør', value: 'Fast tilknyttet chauffør' },
          { label: 'Fordel', value: 'Økonomisk optimeret aftale' }
        ];
      case 'logistik':
        return [
          { label: 'Ydelse', value: 'Komplet logistikstyring' },
          { label: 'Håndtering', value: 'Effektiv omlastning & koordinering' },
          { label: 'Integration', value: 'Sømløs med jeres drift' },
          { label: 'Skalering', value: 'Fleksibel efter jeres behov' }
        ];
      default:
        return [
          { label: 'Dækningsområde', value: 'Hele landet' },
          { label: 'Sikkerhed', value: '100% forsikret transport' }
        ];
    }
  };

  return (
    <div className="w-full bg-slate-50">
      {/* Side Header */}
      <section className="bg-brand-dark text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#0056b3_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs sm:text-sm font-bold text-brand-primary uppercase tracking-widest bg-brand-primary/10 px-3 py-1.5 rounded-full border border-brand-primary/20">
            Professionelle løsninger
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight mt-4">
            Vores ydelser
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto font-sans">
            Vi leverer skræddersyede transport- og logistikløsninger i hele Danmark med fokus på pålidelighed, fleksibilitet og punktlighed.
          </p>
        </div>
      </section>

      {/* Introduktion fra CMS */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="text-slate-600 text-lg leading-relaxed font-sans"
            dangerouslySetInnerHTML={{ __html: cmsPage?.content || '<p>Vi tilbyder en bred vifte af transport- og logistikydelser, herunder kurerkørsel, distribution og skræddersyede logistikløsninger til både små og store virksomheder.</p>' }}
          />
        </div>
      </section>

      {/* Ydelser Sektioner */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {servicesData.map((service, idx) => {
            const IconComponent = service.icon;
            const specs = getServiceSpecs(service.id);
            const isEven = idx % 2 === 0;

            return (
              <div
                key={service.id}
                id={service.id}
                className="scroll-mt-24 border-b border-slate-100 pb-20 last:border-0 last:pb-0"
              >
                <div className={`flex flex-col lg:flex-row gap-12 items-stretch ${
                  isEven ? '' : 'lg:flex-row-reverse'
                }`}>
                  {/* Tekst-kolonne */}
                  <div className="w-full lg:w-3/5 flex flex-col justify-center space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex p-3 rounded-xl bg-brand-primary/10 text-brand-primary">
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <span className="text-xs font-bold text-brand-primary uppercase tracking-wider bg-brand-primary/5 px-3 py-1 rounded-full">
                        Ydelse {idx + 1} af {servicesData.length}
                      </span>
                    </div>
                    
                    <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-primary tracking-tight">
                      {service.title}
                    </h2>
                    
                    <p className="text-slate-600 leading-relaxed text-base sm:text-lg font-sans">
                      {service.longDesc}
                    </p>
                    
                    {/* Features liste */}
                    <div className="space-y-4 pt-2">
                      <h3 className="text-xs font-bold text-brand-dark uppercase tracking-wider">
                        Hvad vi sikrer dig:
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {service.features.map((feature, fIdx) => (
                          <div key={fIdx} className="flex items-start gap-3 text-slate-700 text-sm sm:text-base">
                            <CheckCircle2 className="h-5 w-5 text-brand-primary shrink-0 mt-0.5" />
                            <span className="font-medium">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Faktaboks / Info-kort i stedet for billede */}
                  <div className="w-full lg:w-2/5 flex">
                    <div className="w-full bg-slate-50 rounded-2xl border border-slate-200/70 p-8 flex flex-col justify-between shadow-sm relative overflow-hidden">
                      {/* Subtil baggrunds-dekorations-cirkel */}
                      <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-brand-primary/5 rounded-full pointer-events-none" />
                      
                      <div className="space-y-6 relative z-10">
                        <div className="border-b border-slate-200 pb-4">
                          <h4 className="text-xs font-bold text-brand-primary uppercase tracking-widest">Hurtige fakta</h4>
                          <p className="text-lg font-serif font-bold text-brand-dark mt-1">Specifikationer</p>
                        </div>

                        <div className="space-y-4">
                          {specs.map((spec, sIdx) => (
                            <div key={sIdx} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 border-b border-slate-200/50 pb-3 last:border-0 last:pb-0">
                              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{spec.label}</span>
                              <span className="text-sm font-semibold text-brand-dark text-left sm:text-right">{spec.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="pt-8 mt-6 border-t border-slate-200 relative z-10 space-y-3">
                        <Link
                          to="/kontakt"
                          className="w-full inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary/95 text-white py-3.5 px-5 rounded-xl text-sm font-bold transition-all duration-200 shadow-sm hover:shadow group"
                        >
                          Indhent uforpligtende tilbud
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <p className="text-center text-xs text-slate-500">
                          Vi svarer normalt inden for få timer på hverdage.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Kontakt CTA */}
      <section className="bg-brand-dark text-white py-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="inline-flex p-3 rounded-full bg-brand-primary/10 text-brand-primary mb-2">
            <PhoneCall className="h-6 w-6" />
          </div>
          <h2 className="text-3xl font-serif font-bold tracking-tight">
            Har du brug for en skræddersyet løsning?
          </h2>
          <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto">
            Vi sidder klar til at sammensætte et uforpligtende tilbud, der passer præcist til din virksomheds transport- og logistikbehov.
          </p>
          <div className="pt-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary/95 text-white px-8 py-4 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors shadow-lg cursor-pointer"
            >
              Kontakt os for et tilbud
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
